"use client";

/**
 * ComponentHighlightOverlay
 *
 * Renders a fixed-position highlight box over every DOM instance of a
 * RenderedComponent while the user hovers a file row in the debug panel.
 *
 * Dev-only — rendered via createPortal so it sits above page content but
 * below the debug panel itself (z-index 2147483644 vs panel's 2147483646).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMounted } from "../hooks/use-is-mounted";
import { getComponentRects } from "../utils/fiber-dom";
import type {
  ComponentHighlightOverlayProps,
  HighlightRect,
} from "./component-highlight-overlay.types";

export function ComponentHighlightOverlay({
  component,
}: ComponentHighlightOverlayProps) {
  const [rects, setRects] = useState<HighlightRect[]>([]);
  const rafRef = useRef<number | null>(null);
  // Holds latest rects so the RAF callback can diff without closing over stale state
  const prevRectsRef = useRef<HighlightRect[]>([]);
  const mounted = useIsMounted();

  useEffect(() => {
    const update = () => {
      const domRects = getComponentRects(component);
      const next = domRects.map((r) => ({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      }));

      // Only call setRects when something actually moved — avoids 60fps re-renders
      // when the highlighted element is not animating.
      const prev = prevRectsRef.current;
      const changed =
        next.length !== prev.length ||
        next.some(
          (r, i) =>
            r.top !== prev[i]?.top ||
            r.left !== prev[i]?.left ||
            r.width !== prev[i]?.width ||
            r.height !== prev[i]?.height,
        );
      if (changed) {
        prevRectsRef.current = next;
        setRects(next);
      }

      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      prevRectsRef.current = [];
    };
  }, [component]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <>
      {rects.map((rect, i) =>
        rect.width === 0 && rect.height === 0 ? null : (
          <div
            key={i}
            data-feedback-capture-exclude
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              zIndex: 2147483644,
              pointerEvents: "none",
              background: "rgba(56, 189, 248, 0.10)",
              border: "2px solid rgba(56, 189, 248, 0.65)",
              borderRadius: 4,
              boxSizing: "border-box",
            }}
          >
            {/* Component name label — top-left corner */}
            {}
            <span
              style={{
                position: "absolute",
                top: -1,
                left: -1,
                background: "rgba(14, 116, 144, 0.92)",
                color: "#fff",
                fontSize: 10,
                lineHeight: "16px",
                padding: "0 5px",
                borderRadius: "3px 0 3px 0",
                fontFamily: "monospace",
                whiteSpace: "nowrap",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {component.name}
            </span>
          </div>
        ),
      )}
    </>,
    document.body,
  );
}
